import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, AtSign, Clapperboard, Package, Paperclip, Square } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { useProductsApi } from '../../../../common/hooks/use-products-api.hook';
import { toCanvasProduct } from '../../content-studio/product-mapping';
import { ProductInfo, productInfoFrom } from './ShotCard';
import { LightboxMedia, MediaLightbox } from '../../../../components/design-system/common/MediaLightbox';
import { MediaChip } from '../../../../components/design-system/common/MediaChip';
import { PinterestLogo, PinterestPickerPanel } from '../../pinterest';

export interface ComposerChip {
    key: string;
    label: string;
    thumbnailUrl?: string;
    kind: 'product' | 'reference';
    busy?: boolean;
    /** What clicking the chip shows, large. */
    preview?: LightboxMedia;
}

interface ComposerProps {
    value: string;
    onChange: (v: string) => void;
    onSubmit: () => void;
    placeholder?: string;
    busy?: boolean;
    onStop?: () => void;
    chips?: ComposerChip[];
    onRemoveChip?: (key: string) => void;
    onAttachReference?: (file: File) => void;
    onPickProducts?: () => void;
    size?: 'lg' | 'md';
    autoFocus?: boolean;
    footer?: React.ReactNode;
    /** Typing "@" searches the catalogue; a chosen product is handed here and shown as a chip. */
    onMentionProduct?: (product: ProductInfo) => void;
}

/** The "@query" being typed just before the caret, or null. One line, a product name's length. */
function mentionAt(value: string, caret: number): { start: number; query: string } | null {
    const head = value.slice(0, caret);
    const m = /(?:^|\s)@([^@\n]{0,40})$/.exec(head);
    if (!m) return null;
    return { start: caret - m[1].length - 1, query: m[1] };
}

/**
 * Where the vendor says what they want. One box, used on the Studio home and
 * in the conversation — the same words work in both, so the same box does.
 *
 * Attachments are the two things a brief needs besides words: which products,
 * and a reference to match. Both show as chips with their picture, inside the
 * box, so what goes with the message is visible before it is sent — and a
 * click on one shows it large.
 */
export const Composer: React.FC<ComposerProps> = ({
    value, onChange, onSubmit, placeholder, busy, onStop, chips = [], onRemoveChip,
    onAttachReference, onPickProducts, size = 'md', autoFocus, footer, onMentionProduct,
}) => {
    const { t } = useTranslation();
    const ref = useRef<HTMLTextAreaElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const productsApi = useProductsApi();
    const lg = size === 'lg';

    // ── "@" mentions ─────────────────────────────
    const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
    const [matches, setMatches] = useState<ProductInfo[]>([]);
    const [searching, setSearching] = useState(false);
    const [active, setActive] = useState(0);
    const searchSeq = useRef(0);

    const readMention = (el: HTMLTextAreaElement) => {
        if (!onMentionProduct) return;
        const m = mentionAt(el.value, el.selectionStart ?? el.value.length);
        // A query that has grown past a space and found nothing is prose, not a product.
        setMention((prev) => (m && !(m.query.includes(' ') && prev && !matches.length && !searching) ? m : null));
    };

    useEffect(() => {
        if (!mention) { setMatches([]); return undefined; }
        const seq = ++searchSeq.current;
        setSearching(true);
        const h = window.setTimeout(async () => {
            try {
                const { products } = await productsApi.findProductsPage({ page: 1, limit: 8, search: mention.query.trim(), draft: 'complete' } as any);
                if (seq !== searchSeq.current) return;
                setMatches((products || []).map(toCanvasProduct).map(productInfoFrom));
                setActive(0);
            } catch {
                if (seq === searchSeq.current) setMatches([]);
            } finally {
                if (seq === searchSeq.current) setSearching(false);
            }
        }, mention.query ? 200 : 0);
        return () => window.clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mention?.query, mention?.start]);

    /**
     * A product picked: it becomes a chip, and the "@query" in the text becomes
     * the product's plain name — so the sentence still reads, and typing on
     * does not search the catalogue again.
     */
    const pick = (p: ProductInfo) => {
        const el = ref.current;
        if (!el || !mention) return;
        const caret = el.selectionStart ?? value.length;
        const written = `${p.name} `;
        const next = value.slice(0, mention.start) + written + value.slice(caret);
        onChange(next);
        onMentionProduct?.(p);
        setMention(null);
        setMatches([]);
        const at = mention.start + written.length;
        requestAnimationFrame(() => { el.focus(); el.setSelectionRange(at, at); });
    };

    /** Type an "@" for the vendor, so the product search opens. */
    const startMention = () => {
        const el = ref.current;
        if (!el) return;
        const caret = el.selectionStart ?? value.length;
        const before = value.slice(0, caret);
        const insert = `${before && !/\s$/.test(before) ? ' ' : ''}@`;
        const next = before + insert + value.slice(caret);
        onChange(next);
        const at = caret + insert.length;
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(at, at);
            setMention({ start: at - 1, query: '' });
        });
    };

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, lg ? 260 : 160)}px`;
    }, [value, lg]);

    const canSend = !!value.trim() && !busy;
    const [dragging, setDragging] = React.useState(false);
    const [previewing, setPreviewing] = useState<LightboxMedia | null>(null);
    // Any pin — a picture or a video — as the reference, like a dropped file.
    const [pinterestOpen, setPinterestOpen] = useState(false);

    /** The first picture or video in a paste or a drop, as a reference. */
    const mediaFrom = (files: FileList | null | undefined): File | null => {
        for (const f of Array.from(files || [])) {
            if (f.type.startsWith('image/') || f.type.startsWith('video/')) return f;
        }
        return null;
    };

    const attachButtons = lg ? (
        <>
            {onPickProducts && (
                <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={onPickProducts}>
                    <Package className="size-3.5" /><span className="hidden sm:inline">{t('STUDIO.ATTACH_PRODUCTS', 'Products')}</span>
                </Button>
            )}
            {onAttachReference && (
                <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={() => fileRef.current?.click()}
                    title={t('STUDIO.ATTACH_REFERENCE', 'A video or picture to copy')}>
                    <Clapperboard className="size-3.5" /><span className="hidden sm:inline">{t('STUDIO.ATTACH_REFERENCE_SHORT', 'Reference')}</span>
                </Button>
            )}
            {onAttachReference && (
                <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={() => setPinterestOpen(true)}
                    title={t('STUDIO.ATTACH_PINTEREST_HINT', 'Use any Pinterest pin as the reference')}>
                    <PinterestLogo size={14} /><span className="hidden sm:inline">{t('STUDIO.ATTACH_PINTEREST', 'Pinterest')}</span>
                </Button>
            )}
        </>
    ) : (onAttachReference || onPickProducts) ? (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" className="text-muted-foreground" aria-label={t('STUDIO.ATTACH', 'Add')}>
                    <Paperclip className="size-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top">
                {onPickProducts && (
                    <DropdownMenuItem onSelect={onPickProducts}><Package className="size-4" />{t('STUDIO.ATTACH_PRODUCTS', 'Products')}</DropdownMenuItem>
                )}
                {onAttachReference && (
                    <DropdownMenuItem onSelect={() => fileRef.current?.click()}><Clapperboard className="size-4" />{t('STUDIO.ATTACH_REFERENCE', 'A video or picture to copy')}</DropdownMenuItem>
                )}
                {onAttachReference && (
                    <DropdownMenuItem onSelect={() => setPinterestOpen(true)}><PinterestLogo size={16} />{t('STUDIO.ATTACH_PINTEREST_HINT', 'Use any Pinterest pin as the reference')}</DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    ) : null;

    return (
        <div
            className={cn(
                'relative rounded-2xl border bg-background transition-[border-color,box-shadow] focus-within:border-foreground/25 focus-within:ring-[3px] focus-within:ring-foreground/[0.05]',
                lg ? 'p-3 shadow-[0_1px_2px_rgba(28,25,23,0.04),0_8px_24px_-12px_rgba(28,25,23,0.10)]' : 'p-2',
                dragging && 'border-dashed border-foreground/50 bg-accent/40',
            )}
            onDragOver={(e) => {
                if (!onAttachReference || !Array.from(e.dataTransfer.types).includes('Files')) return;
                e.preventDefault();
                setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
                setDragging(false);
                const file = mediaFrom(e.dataTransfer.files);
                if (!file || !onAttachReference) return;
                e.preventDefault();
                onAttachReference(file);
            }}
        >
            {chips.length > 0 && (
                <div className={cn('flex flex-wrap gap-1.5', lg ? 'px-1 pb-2.5' : 'px-0.5 pb-2')}>
                    {chips.map((c) => (
                        <MediaChip
                            key={c.key}
                            label={c.busy ? t('STUDIO.READING', 'Reading…') : c.label}
                            busy={c.busy}
                            thumbnailUrl={c.thumbnailUrl}
                            videoUrl={!c.thumbnailUrl && c.preview?.kind === 'video' ? c.preview.url : undefined}
                            icon={c.kind === 'reference' ? <Clapperboard /> : <Package />}
                            onOpen={c.preview ? () => setPreviewing(c.preview!) : undefined}
                            openLabel={t('STUDIO.OPEN_PREVIEW', 'Preview')}
                            onRemove={onRemoveChip ? () => onRemoveChip(c.key) : undefined}
                            removeLabel={t('STUDIO.REMOVE', 'Remove')}
                        />
                    ))}
                </div>
            )}
            <textarea
                ref={ref}
                value={value}
                autoFocus={autoFocus}
                rows={lg ? 2 : 1}
                placeholder={placeholder}
                onChange={(e) => { onChange(e.target.value); readMention(e.target); }}
                onClick={(e) => readMention(e.currentTarget)}
                onBlur={() => window.setTimeout(() => setMention(null), 150)}
                onPaste={(e) => {
                    // A pasted picture or video is a reference, like one attached.
                    const file = mediaFrom(e.clipboardData?.files);
                    if (!file || !onAttachReference) return;
                    e.preventDefault();
                    onAttachReference(file);
                }}
                onKeyDown={(e) => {
                    if (mention && matches.length) {
                        if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % matches.length); return; }
                        if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + matches.length) % matches.length); return; }
                        if ((e.key === 'Enter' || e.key === 'Tab') && !e.nativeEvent.isComposing) { e.preventDefault(); pick(matches[active]); return; }
                    }
                    if (mention && e.key === 'Escape') { e.preventDefault(); setMention(null); return; }
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        if (canSend) onSubmit();
                    }
                }}
                className={cn(
                    'block w-full resize-none bg-transparent outline-none placeholder:text-muted-foreground/80',
                    lg ? 'min-h-[56px] px-1.5 text-[15px] leading-relaxed' : 'px-1 text-sm leading-relaxed',
                )}
            />
            <div className={cn('flex items-center gap-1', lg ? 'pt-2' : 'pt-1.5')}>
                {attachButtons}
                {onMentionProduct && lg && (
                    <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={startMention}
                        title={t('STUDIO.MENTION.HINT', 'Type @ to name a product')}>
                        <AtSign className="size-3.5" /><span className="hidden sm:inline">{t('STUDIO.MENTION.BUTTON', 'Mention')}</span>
                    </Button>
                )}
                {mention && (
                    <div
                        role="listbox"
                        className={cn(
                            'absolute left-2 right-2 z-50 max-h-72 overflow-y-auto rounded-xl border bg-popover p-1 text-popover-foreground shadow-lg',
                            lg ? 'top-full mt-1.5' : 'bottom-full mb-1.5',
                        )}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <div className="flex items-center gap-2 px-2 pb-1 pt-1.5 text-xs text-muted-foreground">
                            {t('STUDIO.MENTION.TITLE', 'Products')}
                            {searching && <Spinner className="size-3" />}
                        </div>
                        {matches.length === 0 && !searching ? (
                            <div className="px-2 py-2 text-sm text-muted-foreground">
                                {mention.query ? t('STUDIO.MENTION.NONE', 'No product matches “{{q}}”', { q: mention.query }) : t('STUDIO.MENTION.TYPE', 'Type a product name')}
                            </div>
                        ) : matches.map((p, i) => (
                            <button
                                key={p.uuid}
                                type="button"
                                role="option"
                                aria-selected={i === active}
                                onMouseEnter={() => setActive(i)}
                                onClick={() => pick(p)}
                                className={cn('flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm', i === active && 'bg-accent')}
                            >
                                {p.thumbnailUrl
                                    ? <img src={p.thumbnailUrl} alt="" className="size-8 shrink-0 rounded-md bg-muted object-cover" />
                                    : <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted"><Package className="size-4 text-muted-foreground" /></span>}
                                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                            </button>
                        ))}
                    </div>
                )}
                <MediaLightbox media={previewing} onClose={() => setPreviewing(null)} />
                {onAttachReference && (
                    <PinterestPickerPanel open={pinterestOpen} onClose={() => setPinterestOpen(false)} onPick={async (file) => { onAttachReference(file); }} />
                )}
                <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm,image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onAttachReference?.(f); }} />
                <div className="flex min-w-0 flex-1 items-center gap-1.5">{footer}</div>
                {busy && onStop ? (
                    <Button variant="outline" size="icon-sm" className="rounded-full" onClick={onStop} aria-label={t('STUDIO.STOP', 'Stop')}><Square className="size-3" /></Button>
                ) : (
                    <Button
                        variant="outline"
                        size={lg ? 'icon' : 'icon-sm'}
                        onClick={onSubmit}
                        disabled={!canSend}
                        aria-label={t('STUDIO.SEND', 'Send')}
                        className={cn('rounded-full transition-colors', canSend && 'border-foreground/40 bg-accent text-foreground hover:bg-accent/70')}
                    >
                        {busy ? <Spinner className="size-4" /> : <ArrowUp className="size-4" />}
                    </Button>
                )}
            </div>
        </div>
    );
};
